import { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router';

import { TileCarousel } from 'components/TileCarousel/TileCarousel';
import { UnpublishedListing } from 'components/UnpublishedListing/UnpublishedListing';
import { useFetchFn } from 'hooks/useFetchFn';
import { useSetDocumentTitle } from 'hooks/useSetDocumentTitle';
import type { ApiUnpublishedEvent } from 'modules/listing/api/event/types/ApiUnpublishedEvent';
import { searchAlgoliaFetchRecommendedListings } from 'modules/search/algolia/fetch/searchAlgoliaFetchRecommendedListings';
import { useSearchContextHeader } from 'modules/search/containers/SearchContextHeader/useSearchContextHeader';
import { getParsedSearchQuery } from 'modules/search/helpers/getParsedSearchQuery';
import type { SearchPageLocationState } from 'modules/search/pages/Search/types/SearchPageLocationState';
import { searchGetEventsRoutePath } from 'modules/search/routing/helpers/searchGetEventsRoutePath';
import { trackListing } from 'utils/analytics/trackListing';

import { getUnpublishedEventPageSeeMoreText } from './getUnpublishedEventPageSeeMoreText';

type Props = {
  unpublishedEvent: ApiUnpublishedEvent;
};

export function UnpublishedEventPage({ unpublishedEvent }: Props) {
  useSetDocumentTitle(getText('Listing Unpublished'));

  const { listingName, id, org } = unpublishedEvent;

  useEffect(() => {
    trackListing({
      status: 'unpublished',
      hasAts: false,
      listingName,
      listingId: id,
      listingType: 'EVENT',
      orgId: org ? org.id : undefined,
      orgName: org ? org.name : undefined,
      orgType: org ? org.orgType : undefined,
    });
  }, [id, listingName, org]);

  const { searchContext } = useSearchContextHeader({
    currentObjectId: unpublishedEvent.id,
  });

  const [recommendedListingsResult] = useFetchFn({
    fn: searchAlgoliaFetchRecommendedListings,
    args: [unpublishedEvent],
  });

  const recommendedListings = recommendedListingsResult?.hits ?? [];

  const navigate = useNavigate();
  const search = useCallback(
    (query: string) => {
      const { parsedQuery, locationType, remoteKeywords } =
        getParsedSearchQuery({ query });

      navigate(
        searchGetEventsRoutePath({
          lang: CURRENT_LOCALE(),
          q: parsedQuery,
          filtersByName: { locationType },
        }),
        {
          state: {
            searchFrom: 'Unpublished Event Page',
            remoteKeywords,
          } satisfies SearchPageLocationState,
        },
      );
    },
    [navigate],
  );

  const seeMoreText = getUnpublishedEventPageSeeMoreText({
    orgUrl: unpublishedEvent.org
      ? `${unpublishedEvent.org.url[CURRENT_LOCALE()]}#opportunities`
      : undefined,
    orgPublishedEventsCount: unpublishedEvent.orgPublishedEventsCount ?? 0,
  });

  return (
    <UnpublishedListing
      searchContext={searchContext}
      footerTitle={getText('Search Events')}
      seeMoreText={seeMoreText}
      searchFormAnalyticsTitle="[main] View Event Page (Unpublished) - search"
      onSubmit={({ values }) => search(values.query)}
      exploreSection={
        recommendedListings.length > 0 && (
          <TileCarousel
            headline={getText('Explore Similar Events on Idealist')}
            results={recommendedListings}
            seeAll={{
              text: getText('See all events'),
              action: { type: 'button', onClick: () => search('') },
            }}
            listingType="EVENT"
            carouselLocation="Event Listing"
            fromListingId={unpublishedEvent.id}
          />
        )
      }
    />
  );
}
